import { useCallback, useRef } from 'react';
import {
  defaultDefinitionWidgets,
  OpenApiDefinitionWidget,
} from '@backstage/plugin-api-docs';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TestJwtMintControl } from './TestJwtMintControl';

// The bits of the Swagger UI system we touch.
type SwaggerSystem = {
  authSelectors: { definitionsToAuthorize: () => any };
  authActions: {
    authorize: (payload: object) => void;
    authorizeOauth2: (payload: object) => void;
  };
};

const schemesOf = (system: SwaggerSystem): [string, any][] =>
  (system.authSelectors.definitionsToAuthorize()?.toJS?.() ?? []).flatMap(
    (entry: Record<string, any>) => Object.entries(entry),
  );

/**
 * Pre-authorizes every bearer-capable security scheme with the token.
 * Returns false when the spec has none, so the caller falls back to
 * injecting the header itself.
 */
const applyToken = (system: SwaggerSystem, token: string): boolean => {
  let applied = false;
  for (const [name, schema] of schemesOf(system)) {
    if (schema.type === 'http' && /^bearer$/i.test(schema.scheme)) {
      system.authActions.authorize({ [name]: { name, schema, value: token } });
      applied = true;
    } else if (schema.type === 'oauth2' || schema.type === 'openIdConnect') {
      system.authActions.authorizeOauth2({
        auth: { name, schema },
        token: { access_token: token, token_type: 'Bearer' },
      });
      applied = true;
    }
  }
  return applied;
};

export const OpenApiWithTestJwt = ({ definition }: { definition: string }) => {
  const { entity } = useEntity();
  // Test tokens are only for dev APIs; the backend enforces this too.
  const isDev = String(entity.spec?.lifecycle).toLowerCase() === 'dev';
  const system = useRef<SwaggerSystem>();
  // Set only when the spec has no usable scheme; read by the interceptor.
  const fallbackToken = useRef<string>();

  const onToken = useCallback((token: string) => {
    fallbackToken.current =
      system.current && applyToken(system.current, token) ? undefined : token;
  }, []);

  const requestInterceptor = useCallback((req: any) => {
    if (fallbackToken.current) {
      req.headers = {
        ...req.headers,
        Authorization: `Bearer ${fallbackToken.current}`,
      };
    }
    return req;
  }, []);

  return (
    <>
      {isDev && (
        <TestJwtMintControl
          entityRef={stringifyEntityRef(entity)}
          onToken={onToken}
        />
      )}
      <OpenApiDefinitionWidget
        definition={definition}
        requestInterceptor={requestInterceptor}
        // Passed through to swagger-ui-react; not in the widget's prop types.
        {...({
          onComplete: (s: SwaggerSystem) => {
            system.current = s;
          },
        } as {})}
      />
    </>
  );
};

/** api-docs widgets with OpenAPI swapped for the test-JWT-aware one. */
export const testJwtDefinitionWidgets = () =>
  defaultDefinitionWidgets().map(widget =>
    widget.type === 'openapi'
      ? {
          ...widget,
          component: (definition: string) => (
            <OpenApiWithTestJwt definition={definition} />
          ),
        }
      : widget,
  );
